import { z } from "zod";

const errorSchema = z.object({ error: z.union([z.string(), z.object({ message: z.string() })]) });

export class WorkflowBuilderApiError extends Error {
    constructor(message: string, public readonly status: number) {
        super(message);
        this.name = "WorkflowBuilderApiError";
    }
}

export async function readWorkflowBuilderResponse(response: Response): Promise<unknown> {
    // Keep body reads outside the parsing catch so an aborted request stays aborted.
    const body = await response.text();
    const httpError = `Workflow request failed (${response.status}).`;
    if (!body.trim()) {
        throw new WorkflowBuilderApiError(
            response.ok ? `Workflow service returned an empty response (${response.status}).` : httpError,
            response.status
        );
    }

    let payload: unknown;
    try {
        payload = JSON.parse(body);
    } catch {
        throw new WorkflowBuilderApiError(
            response.ok ? `Workflow service returned an invalid JSON response (${response.status}).` : httpError,
            response.status
        );
    }

    if (!response.ok) {
        const parsed = errorSchema.safeParse(payload);
        const error = parsed.success ? parsed.data.error : undefined;
        throw new WorkflowBuilderApiError(typeof error === "string" ? error : error?.message ?? httpError, response.status);
    }
    return payload;
}

/** DELETE confirms completion with 204; other successful statuses do not confirm removal. */
export async function readWorkflowBuilderDeleteResponse(response: Response): Promise<void> {
    if (response.status === 204) return;
    await readWorkflowBuilderResponse(response);
    throw new WorkflowBuilderApiError(`Workflow service returned an unexpected deletion response (${response.status}).`, response.status);
}
